import React, { useState, useEffect } from 'react';
import { 
  Container, Card, Button, Alert, Spinner, InputGroup, Form, Row, Col, Modal, Table, Badge 
} from 'react-bootstrap';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaPlus, FaSearch, FaFilter, FaSortAmountDown, FaArrowLeft, FaEdit, FaEye, FaCheckCircle } from 'react-icons/fa';
import MainLayout from '../../layouts/MainLayout';
import { fetchUsersByRoles, formatUserForDropdown } from '../../utils/userUtils';
import mockRnDService from '../../services/mockRnDService';

const ProjectList = ({ showModalOnLoad = false }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(showModalOnLoad);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: new Date().toISOString().split('T')[0],
    target_date: '',
    priority: 'medium',
    manager: ''
  });

  // Update the managers state
  const [managers, setManagers] = useState([]);

  // Add a new useEffect specifically for fetching managers
  useEffect(() => {
    const loadManagers = async () => {
      // Role names to include
      const roleNames = ['owner', 'admin', 'manager', 'rnd', 'spv rnd', 'rnd manager', 'general manager'];
      
      // Fetch users with specified roles
      const users = await fetchUsersByRoles(roleNames);
      
      // Format users for dropdown
      const formattedManagers = users.map(formatUserForDropdown);
      
      // Set managers or use fallback data if empty
      if (formattedManagers.length > 0) {
        setManagers(formattedManagers);
      } else {
        // Fallback data
        setManagers([
          { id: 1, name: 'Muhammad Botee', role: 'Owner' },
          { id: 2, name: 'Fatkhu Rozak', role: 'RnD Manager' },
          { id: 3, name: 'Andi', role: 'RnD Staff' },
          { id: 4, name: 'Budi', role: 'SPV RnD' },
          { id: 5, name: 'Admin', role: 'Admin' }
        ]);
      }
    };
    
    loadManagers();
  }, []);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    setError(null);

    try {
      const activeProjects = await mockRnDService.getActiveProjects();
      setProjects(activeProjects);
    } catch (err) {
      console.error("Error fetching projects:", err);
      setError("Terjadi kesalahan saat memuat data proyek.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create project using mock service
      const newProject = await mockRnDService.createProject(formData);
      
      // Add to list of projects in state
      setProjects(prev => [newProject, ...prev]);
      
      toast.success('Proyek berhasil ditambahkan!');
      
      // Reset form & close modal
      setFormData({
        name: '',
        description: '',
        start_date: new Date().toISOString().split('T')[0],
        target_date: '',
        priority: 'medium',
        manager: ''
      });
      setShowAddModal(false);
    } catch (err) {
      console.error("Error adding project:", err);
      toast.error(`Gagal menambahkan proyek: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const filteredProjects = () => {
    return projects
      .filter(project => {
        // Apply search term filter
        const searchMatch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           project.description.toLowerCase().includes(searchTerm.toLowerCase());
        
        // Apply priority filter
        const priorityMatch = filterPriority === 'all' || project.priority === filterPriority;
        
        return searchMatch && priorityMatch;
      })
      .sort((a, b) => {
        // Apply sorting
        let comparison = 0;
        
        if (sortBy === 'name') {
          comparison = a.name.localeCompare(b.name);
        } else if (sortBy === 'date') {
          comparison = new Date(a.start_date) - new Date(b.start_date);
        } else if (sortBy === 'priority') {
          const priorityOrder = { 'high': 1, 'medium': 2, 'low': 3 };
          comparison = priorityOrder[a.priority] - priorityOrder[b.priority];
        }
        
        return sortOrder === 'asc' ? comparison : -comparison;
      });
  };
  
  const getBadgeVariant = (priority) => {
    switch (priority.toLowerCase()) {
      case 'high': return 'danger';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'secondary';
    }
  };

  const handleViewProgress = (projectId) => {
    navigate(`/rnd/projects/${projectId}/progress`);
  };

  return (
    <MainLayout>
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Daftar Proyek R&D Aktif</h2>
          <div>
            <Button variant="primary" className="me-2" as={Link} to="/dashboard/rnd">
              <FaArrowLeft className="me-1" /> Kembali ke Dashboard
            </Button>
            <Button variant="success" onClick={() => setShowAddModal(true)}>
              <FaPlus className="me-1" /> Tambah Proyek Baru
            </Button>
          </div>
        </div>

        {error && <Alert variant="warning">{error}</Alert>}

        {/* Search and Filters */}
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={6} className="mb-3 mb-md-0">
                <InputGroup>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Cari berdasarkan nama atau deskripsi..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3} className="mb-3 mb-md-0">
                <InputGroup>
                  <InputGroup.Text>
                    <FaFilter />
                  </InputGroup.Text>
                  <Form.Select
                    value={filterPriority}
                    onChange={(e) => setFilterPriority(e.target.value)}
                  >
                    <option value="all">Semua Prioritas</option>
                    <option value="high">Prioritas Tinggi</option>
                    <option value="medium">Prioritas Menengah</option>
                    <option value="low">Prioritas Rendah</option>
                  </Form.Select>
                </InputGroup>
              </Col>
              <Col md={3}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaSortAmountDown />
                  </InputGroup.Text>
                  <Form.Select
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                      const [newSortBy, newSortOrder] = e.target.value.split('-');
                      setSortBy(newSortBy);
                      setSortOrder(newSortOrder);
                    }}
                  >
                    <option value="name-asc">Nama (A-Z)</option>
                    <option value="name-desc">Nama (Z-A)</option>
                    <option value="date-asc">Tanggal (Terlama)</option>
                    <option value="date-desc">Tanggal (Terbaru)</option>
                    <option value="priority-asc">Prioritas (Rendah-Tinggi)</option>
                    <option value="priority-desc">Prioritas (Tinggi-Rendah)</option>
                  </Form.Select>
                </InputGroup>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Project List */}
        <Card>
          <Card.Header className="bg-white">
            <h4>Proyek Aktif ({filteredProjects().length})</h4>
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="text-center p-5">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-3">Memuat data proyek...</p>
              </div>
            ) : (
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>Nama Proyek</th>
                    <th>Tanggal Mulai</th>
                    <th>Target Selesai</th>
                    <th>Status</th>
                    <th>Prioritas</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects().length > 0 ? (
                    filteredProjects().map((project) => (
                      <tr key={project.id}>
                        <td>
                          <Link to={`/rnd/projects/${project.id}`} className="text-dark">
                            <strong>{project.name}</strong>
                          </Link>
                          <div className="text-muted small">
                            {project.description || "Tidak ada deskripsi"}
                          </div>
                        </td>
                        <td>{project.start_date}</td>
                        <td>{project.target_date}</td>
                        <td>
                          <Badge bg="primary">Aktif</Badge>
                        </td>
                        <td>
                          <Badge bg={getBadgeVariant(project.priority)}>
                            {project.priority === 'high' ? 'Tinggi' : 
                             project.priority === 'medium' ? 'Menengah' : 'Rendah'}
                          </Badge>
                        </td>
                        <td>
                          <Button 
                            variant="outline-info" 
                            size="sm" 
                            className="me-1" 
                            as={Link} 
                            to={`/rnd/projects/${project.id}`}
                          >
                            <FaEye /> Detail
                          </Button>
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            className="me-1" 
                            as={Link} 
                            to={`/rnd/projects/${project.id}/edit`}
                          >
                            <FaEdit /> Edit
                          </Button>
                          <Button 
                            variant="outline-success" 
                            size="sm" 
                            onClick={() => navigate(`/rnd/projects/${project.id}/progress`)}
                          >
                            <FaCheckCircle /> Progress
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center py-4">
                        Tidak ada proyek aktif saat ini.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* Add Project Modal */}
        <Modal 
          show={showAddModal} 
          onHide={() => setShowAddModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Tambah Proyek Baru</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleAddProject}>
              <Form.Group className="mb-3">
                <Form.Label>Nama Proyek <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Deskripsi Proyek</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Tanggal Mulai <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Target Selesai <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="date"
                      name="target_date"
                      value={formData.target_date}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Prioritas</Form.Label>
                    <Form.Select
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                    >
                      <option value="high">Tinggi</option>
                      <option value="medium">Menengah</option>
                      <option value="low">Rendah</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Manager</Form.Label>
                    <Form.Select
                      name="manager"
                      value={formData.manager}
                      onChange={handleChange}
                    >
                      <option value="">-- Pilih Manager --</option>
                      {managers.map(manager => (
                        <option key={manager.id} value={manager.id}>
                          {manager.name} ({manager.role})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-end mt-3">
                <Button 
                  variant="secondary" 
                  onClick={() => setShowAddModal(false)}
                  className="me-2"
                >
                  Batal
                </Button>
                <Button 
                  type="submit" 
                  variant="primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-1"
                      />
                      Menyimpan...
                    </>
                  ) : (
                    'Simpan Proyek'
                  )}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </Container>
    </MainLayout>
  );
};

export default ProjectList;